/** FIREBASE_PROJECT_SETUP control — a gated, three-step Firebase setup flow.
 *
 *  Fetches the operator's LIVE Firebase status (firebase_status) and drives the whole UI off
 *  the returned `detail`. Nothing renders until automation is configured on the server.
 *
 *    Gate:    detail.configured === false → "not configured" info Alert, stop.
 *    Step 1:  Project — pick/link an EXISTING project (Autocomplete over the projects the
 *             automation account can access, + an "Import a project by ID" affordance for one
 *             not in the list) OR, when org-level auto-create is available, CREATE one.
 *             Shown only while the project isn't set (or the operator hits "Change project").
 *             Once set, it collapses to a compact summary (project id + source + Sync / Change).
 *    Step 2:  Apps + config — create the Android + iOS apps (idempotent) and download the
 *             google-services.json / GoogleService-Info.plist (returned as TEXT → Blob here).
 *    Step 3:  Testers — replace the App Distribution tester list.
 *
 *  Steps 2 & 3 are GATED behind a set project (detail.completion.project_set) — you can't create
 *  apps or add testers before the project exists. Sync (sync_firebase) reconciles against the
 *  live project and recomputes every Google item's status → onChanged() refreshes the whole list.
 *
 *  Edits are held as an overlay in state and fall back to the server-returned detail (same idiom
 *  as BranchConfigEditor). Never renders a secret; all values come from the server.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { FirebaseDetail, FirebaseProject, PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { firebaseProjectActions } from '../tenantLinks';

const COMPLETION_LABELS: Record<string, string> = {
  project_set: 'Project',
  android_app_created: 'Android app',
  ios_app_created: 'iOS app',
  testers_present: 'Testers',
};

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ConsoleApiError ? e.message : fallback;
}

/** Human-readable text for a failed op — the server's error `detail` (a string on the
 *  reason=error path) wins, else the short reason code, else a generic fallback. */
function opErrorText(res: { reason?: string | null; detail?: unknown }): string {
  return (typeof res.detail === 'string' && res.detail) || res.reason || 'Operation failed.';
}

/** True hard-error text from the live status probe (ok=false with a Google detail), if any. */
function statusErrorText(s: { ok: boolean; reason?: string | null; detail?: unknown } | undefined): string | null {
  if (s && s.ok === false && s.reason === 'error' && typeof s.detail === 'string') return s.detail;
  return null;
}

/** Whether a failed save looks like the automation account lacking access to the project
 *  (a mutation result or a thrown ConsoleApiError). Drives the "run setup_google_projects" hint. */
function looksPermissionError(res: { reason?: string | null; detail?: unknown } | undefined, e?: unknown): boolean {
  const text = `${res?.reason ?? ''} ${typeof res?.detail === 'string' ? res.detail : ''}`.toUpperCase();
  if (res?.reason === 'error' && (text.includes('PERMISSION') || text.includes('NOT HAVE PERMISSION'))) return true;
  if (e instanceof ConsoleApiError) {
    if (e.status === 403) return true;
    const m = e.message.toUpperCase();
    if (m.includes('PERMISSION') || m.includes('NOT HAVE PERMISSION')) return true;
  }
  return false;
}

/** The admin fix-up hint shown when an import fails on access (interpolates the entered id). */
function setupHint(projectId: string): string {
  return (
    `The automation account can't access this project yet — an admin must run:  ` +
    `python manage.py setup_google_projects ${projectId}  — then Import again.`
  );
}

/** A slug candidate for a new project id from the display name (editable suggestion). */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
}

/** Build a Blob from the returned file text and trigger a browser download. */
function triggerDownload(filename: string, contentType: string, content: string): void {
  const blob = new Blob([content], { type: contentType || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function FirebaseProjectSetupControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();

  // The action detail() ships inside the item; the live probe augments/replaces it.
  const seed = item.detail as unknown as FirebaseDetail;
  const query = useQuery({
    queryKey: ['preflight', 'firebaseStatus', topId, item.id],
    queryFn: () => items.fetchFirebaseStatus(topId, item.id),
    enabled: canManage,
  });
  const status = query.data;
  const detail = status?.detail ?? seed;
  const configured = Boolean(detail?.configured);
  const projectSet = detail?.completion?.project_set === true;

  // ---- local UI state
  // Step-1 mode: default to "existing"; "auto" only reachable when auto_create_available.
  const [projectMode, setProjectMode] = useState<'existing' | 'auto'>('existing');
  // Existing-mode Autocomplete selection (project id string; freeSolo, so may be typed).
  const [existingId, setExistingId] = useState('');
  // Import-by-ID affordance (a separate project not in the list).
  const [importId, setImportId] = useState('');
  const [importExpanded, setImportExpanded] = useState(false);
  // Auto-mode fields, seeded lazily from detail.
  const [autoProjectId, setAutoProjectId] = useState('');
  const [autoDisplayName, setAutoDisplayName] = useState('');
  // Re-show the Step-1 chooser even though a project is already set.
  const [changingProject, setChangingProject] = useState(false);
  // testers: null overlay = "not touched, show server list"; array = user-edited.
  const [testerEdits, setTesterEdits] = useState<string[] | null>(null);
  // Step-2 app fields — null = untouched (fall back to server detail), string = user-edited.
  const [androidPkg, setAndroidPkg] = useState<string | null>(null);
  const [iosBundle, setIosBundle] = useState<string | null>(null);
  const [appStoreId, setAppStoreId] = useState('');
  // Persistent, dismissible banner for the most recent operation failure (in addition to
  // the transient snackbar) so ops can read/act on the actual Google error.
  const [opError, setOpError] = useState<string | null>(null);

  // Projects the automation account can access (existing-project picker source).
  const projectsQuery = useQuery({
    queryKey: ['preflight', 'firebaseProjects', topId, item.id],
    queryFn: () => items.fetchFirebaseProjects(topId, item.id),
    enabled: canManage && configured,
  });
  const projectsRes = projectsQuery.data;
  const projectOptions: FirebaseProject[] = projectsRes?.ok ? (projectsRes.projects ?? []) : [];
  const projectsUnavailable = projectsRes && !projectsRes.ok ? opErrorText(projectsRes) : null;

  const testers = testerEdits ?? detail?.testers ?? [];

  const clearOverlays = () => {
    setExistingId('');
    setImportId('');
    setImportExpanded(false);
    setAutoProjectId('');
    setAutoDisplayName('');
    setChangingProject(false);
    setTesterEdits(null);
    setOpError(null); // a successful op clears any prior error banner
  };

  const applyResult = (d: FirebaseDetail | undefined) => {
    // Server state changed → drop overlays and refetch so displays reflect the truth.
    clearOverlays();
    if (d) void query.refetch();
    onChanged();
  };

  // Save the project. `origin` differentiates the Import affordance (which shows a setup hint on
  // a permission error) from Link / Create. Both existing paths send mode:'existing'.
  const saveProject = useMutation({
    mutationFn: (vars: {
      mode: 'existing' | 'auto';
      project_id: string;
      display_name?: string;
      origin: 'link' | 'import' | 'auto';
    }) =>
      items.saveFirebaseProject(topId, item.id, {
        mode: vars.mode,
        project_id: vars.project_id,
        display_name: vars.display_name,
      }),
    onMutate: () => setOpError(null),
    onSuccess: (res, vars) => {
      if (res.ok) {
        snackbar.show('Firebase project saved.', 'success');
        applyResult(res.detail);
      } else {
        const base = opErrorText(res);
        const withHint =
          vars.origin === 'import' && looksPermissionError(res)
            ? `${base}\n\n${setupHint(vars.project_id)}`
            : base;
        setOpError(withHint);
        snackbar.show(res.reason ? `Could not save the project: ${res.reason}` : 'Save failed.', 'error');
      }
    },
    onError: (e, vars) => {
      const base = e instanceof ConsoleApiError ? e.message : 'Operation failed.';
      const withHint =
        vars.origin === 'import' && looksPermissionError(undefined, e)
          ? `${base}\n\n${setupHint(vars.project_id)}`
          : base;
      setOpError(withHint);
      snackbar.show(errMessage(e, 'Save failed.'), 'error');
    },
  });

  const createApps = useMutation({
    mutationFn: (vars: { package_name?: string; bundle_id?: string; app_store_id?: string }) =>
      items.createFirebaseApps(topId, item.id, vars),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('Firebase app created.', 'success');
        applyResult(res.detail);
      } else {
        setOpError(opErrorText(res));
        snackbar.show(res.reason === 'no_project' ? 'Set a Firebase project first.' : 'Create failed.', 'error');
      }
    },
    onError: (e) => {
      setOpError(e instanceof ConsoleApiError ? e.message : 'Operation failed.');
      snackbar.show(errMessage(e, 'Create failed.'), 'error');
    },
  });

  const download = useMutation({
    mutationFn: (platform: 'android' | 'ios') => items.downloadFirebaseConfig(topId, item.id, platform),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok && res.content != null && res.filename) {
        triggerDownload(res.filename, res.content_type ?? 'application/octet-stream', res.content);
        setOpError(null);
      } else {
        setOpError(opErrorText(res));
        snackbar.show(res.detail || 'Could not download the config file.', 'error');
      }
    },
    onError: (e) => {
      setOpError(e instanceof ConsoleApiError ? e.message : 'Operation failed.');
      snackbar.show(errMessage(e, 'Download failed.'), 'error');
    },
  });

  const saveTesters = useMutation({
    mutationFn: () =>
      items.saveFirebaseTesters(topId, item.id, {
        emails: testers.map((t) => t.trim()).filter((t) => t !== ''),
      }),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('Testers saved.', 'success');
        applyResult(res.detail);
      } else {
        setOpError(opErrorText(res));
        snackbar.show(res.reason === 'no_project' ? 'Set a Firebase project first.' : 'Save failed.', 'error');
      }
    },
    onError: (e) => {
      setOpError(e instanceof ConsoleApiError ? e.message : 'Operation failed.');
      snackbar.show(errMessage(e, 'Save failed.'), 'error');
    },
  });

  const sync = useMutation({
    mutationFn: () => items.syncFirebase(topId, item.id),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('Synced from Firebase.', 'success');
        applyResult(res.detail); // recomputes statuses across items → refresh the whole list
      } else {
        setOpError(opErrorText(res));
        snackbar.show('Sync failed.', 'error');
      }
    },
    onError: (e) => {
      setOpError(e instanceof ConsoleApiError ? e.message : 'Operation failed.');
      snackbar.show(errMessage(e, 'Sync failed.'), 'error');
    },
  });

  const busy =
    saveProject.isPending || createApps.isPending || download.isPending || saveTesters.isPending || sync.isPending;

  // ---- gate: automation not configured on the server
  if (!configured) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">
          Firebase automation isn&apos;t configured on the server (no Google service account) — this step must
          be completed manually in the Firebase console.
        </Alert>
      </Box>
    );
  }

  const completion = detail.completion ?? {};
  // Persistent banner: a fresh operation error wins; else surface a hard status probe error.
  const bannerError = opError ?? statusErrorText(status);
  const androidExists = Boolean(detail.apps?.android_app_id);
  const iosExists = Boolean(detail.apps?.ios_app_id);
  const testersDirty = testerEdits !== null;
  const autoAvailable = detail.auto_create_available === true;
  // Step 1 chooser shows while the project is unset, or when the operator asks to change it.
  const showStep1 = !projectSet || changingProject;

  const setTester = (i: number, v: string) => setTesterEdits(testers.map((r, j) => (j === i ? v : r)));
  const addTester = () => setTesterEdits([...testers, '']);
  const removeTester = (i: number) => setTesterEdits(testers.filter((_, j) => j !== i));

  // Prefill for uncontrolled-until-touched Step-2 fields (fall back to server detail).
  const androidPkgDefault = detail.apps?.android_package_name ?? detail.android_package_name_default ?? '';
  // The iOS field used to arrive EMPTY while Android prefilled, so ops had to know
  // the runbook's identifier recipe by heart — and the reconcile pass, which had no
  // value either, skipped the iOS app without saying so.
  const iosBundleDefault = detail.apps?.ios_bundle_id ?? detail.ios_bundle_id_default ?? '';
  const androidPkgVal = androidPkg ?? androidPkgDefault;
  const iosBundleVal = iosBundle ?? iosBundleDefault;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" color="info" variant="outlined" label={detail.environment || 'unknown env'} />
          <Typography variant="caption" color="text.secondary">
            Configuring the <strong>{detail.environment}</strong> app.
          </Typography>
        </Stack>

        {bannerError && (
          <Alert
            severity="error"
            onClose={() => setOpError(null)}
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {bannerError}
          </Alert>
        )}

        {query.isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading the live Firebase status…
            </Typography>
          </Box>
        )}

        {!canManage && (
          <Typography variant="caption" color="text.secondary">
            Manage permission is required to load + edit the Firebase configuration.
          </Typography>
        )}

        {/* completion chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {Object.keys(COMPLETION_LABELS).map((k) => {
            const done = completion[k] === true;
            return (
              <Chip
                key={k}
                size="small"
                variant="outlined"
                color={done ? 'success' : 'default'}
                icon={done ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                label={COMPLETION_LABELS[k]}
              />
            );
          })}
        </Box>

        {canManage && (
          <>
            {/* ============================ STEP 1 — Project ============================ */}
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Step 1 · Firebase project
              </Typography>
            </Divider>

            {/* Collapsed summary once a project is set (and not being changed). */}
            {projectSet && !changingProject && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                  Project:{' '}
                  <ValueActions actions={firebaseProjectActions(String(detail.project_id ?? ''))}>
                    <strong>{detail.project_id || '—'}</strong>
                  </ValueActions>
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  label={detail.project_source === 'auto' ? 'Created' : 'Linked'}
                />
                <Button size="small" disabled={busy} onClick={() => sync.mutate()}>
                  Sync from Firebase
                </Button>
                <Button size="small" disabled={busy} onClick={() => setChangingProject(true)}>
                  Change project
                </Button>
              </Stack>
            )}

            {/* Chooser: shown while unset, or when "Change project" was clicked. */}
            {showStep1 && (
              <Stack spacing={1.5}>
                {projectSet && changingProject && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Linking or creating a different project replaces the current one ({detail.project_id}).
                  </Alert>
                )}

                {autoAvailable ? (
                  <RadioGroup
                    row
                    value={projectMode}
                    onChange={(e) => setProjectMode(e.target.value as 'existing' | 'auto')}
                  >
                    <FormControlLabel
                      value="existing"
                      control={<Radio size="small" />}
                      label="Use an existing project"
                    />
                    <FormControlLabel value="auto" control={<Radio size="small" />} label="Create automatically" />
                  </RadioGroup>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Automatic project creation requires org-level setup.
                  </Typography>
                )}

                {/* ---- Existing-project flow */}
                {projectMode === 'existing' && (
                  <Stack spacing={1}>
                    <Autocomplete<FirebaseProject, false, false, true>
                      freeSolo
                      size="small"
                      loading={projectsQuery.isLoading}
                      disabled={busy}
                      options={projectOptions}
                      value={existingId || null}
                      onChange={(_, v) =>
                        setExistingId(v == null ? '' : typeof v === 'string' ? v : v.project_id)
                      }
                      onInputChange={(_, input, reason) => {
                        if (reason === 'input' || reason === 'clear') setExistingId(input);
                      }}
                      getOptionLabel={(o) =>
                        typeof o === 'string' ? o : `${o.display_name} (${o.project_id})`
                      }
                      isOptionEqualToValue={(a, b) => {
                        const ai = typeof a === 'string' ? a : a.project_id;
                        const bi = typeof b === 'string' ? b : b.project_id;
                        return ai === bi;
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Firebase project" placeholder="Select or type a project ID" />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Only lists projects the automation account has access to.
                    </Typography>
                    {projectsUnavailable && (
                      <Typography variant="caption" color="text.secondary">
                        Couldn&apos;t list projects: {projectsUnavailable}. You can still import one by ID below.
                      </Typography>
                    )}
                    <Box>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy || existingId.trim() === ''}
                        onClick={() =>
                          saveProject.mutate({ mode: 'existing', project_id: existingId.trim(), origin: 'link' })
                        }
                      >
                        Link project
                      </Button>
                    </Box>

                    {/* ---- Import a project by ID (not in the list) */}
                    <Accordion
                      disableGutters
                      elevation={0}
                      expanded={importExpanded}
                      onChange={(_, e) => setImportExpanded(e)}
                      sx={{ '&:before': { display: 'none' }, border: (t) => `1px solid ${t.palette.divider}` }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                        <Typography variant="body2">Import a project by ID</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Project ID"
                            value={importId}
                            disabled={busy}
                            onChange={(e) => setImportId(e.target.value)}
                            helperText="Sets up the automation account for a project you know by ID."
                          />
                          <Box>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy || importId.trim() === ''}
                              onClick={() =>
                                saveProject.mutate({
                                  mode: 'existing',
                                  project_id: importId.trim(),
                                  origin: 'import',
                                })
                              }
                            >
                              Import
                            </Button>
                          </Box>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Stack>
                )}

                {/* ---- Auto-create flow (only reachable when auto_create_available) */}
                {projectMode === 'auto' && autoAvailable && (
                  <Stack spacing={1}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Display name"
                        value={autoDisplayName || (detail.app_display_name ?? '')}
                        disabled={busy}
                        onChange={(e) => setAutoDisplayName(e.target.value)}
                        helperText="Defaults to “<operator name> Client”."
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="New project ID"
                        value={autoProjectId}
                        disabled={busy}
                        onChange={(e) => setAutoProjectId(e.target.value)}
                        helperText="Must be globally unique, lowercase letters/digits/hyphens."
                      />
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="text"
                        disabled={busy || (autoDisplayName || detail.app_display_name || '') === ''}
                        onClick={() => setAutoProjectId(slugify(autoDisplayName || detail.app_display_name || ''))}
                      >
                        Suggest ID from name
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Requires org-level projectCreator; if creation fails, use an existing project instead.
                    </Typography>
                    <Box>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy || autoProjectId.trim() === ''}
                        onClick={() =>
                          saveProject.mutate({
                            mode: 'auto',
                            project_id: autoProjectId.trim(),
                            display_name: (autoDisplayName || detail.app_display_name || '').trim() || undefined,
                            origin: 'auto',
                          })
                        }
                      >
                        Create project
                      </Button>
                    </Box>
                  </Stack>
                )}

                {projectSet && changingProject && (
                  <Box>
                    <Button size="small" disabled={busy} onClick={() => setChangingProject(false)}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Stack>
            )}

            {/* ==================== STEP 2 + STEP 3 — gated behind a set project ==================== */}
            {projectSet && (
              <>
                {/* -------------------------- Step 2 · Apps + config -------------------------- */}
                <Divider textAlign="left">
                  <Typography variant="caption" color="text.secondary">
                    Step 2 · Apps &amp; config ({detail.environment})
                  </Typography>
                </Divider>

                {/* Android */}
                {androidExists ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      icon={<CheckCircleIcon fontSize="small" />}
                      label="Android app"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy}
                      onClick={() => download.mutate('android')}
                    >
                      Download google-services.json
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Android package name"
                      value={androidPkgVal}
                      disabled={busy}
                      onChange={(e) => setAndroidPkg(e.target.value)}
                      helperText={`Android app for the ${detail.environment} environment.`}
                    />
                    <Box>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy || androidPkgVal.trim() === ''}
                        onClick={() => createApps.mutate({ package_name: androidPkgVal.trim() })}
                      >
                        Create Android app
                      </Button>
                    </Box>
                  </Stack>
                )}

                {/* iOS */}
                {iosExists ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      icon={<CheckCircleIcon fontSize="small" />}
                      label="iOS app"
                    />
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => download.mutate('ios')}>
                      Download GoogleService-Info.plist
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="iOS bundle ID"
                        value={iosBundleVal}
                        disabled={busy}
                        onChange={(e) => setIosBundle(e.target.value)}
                        helperText={
                          !detail.apps?.ios_bundle_id && iosBundleVal
                            ? `Conventional identifier for ${detail.environment} — confirm it matches Apple before creating.`
                            : `iOS app for the ${detail.environment} environment.`
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="iOS App Store ID (optional)"
                        value={appStoreId}
                        disabled={busy}
                        onChange={(e) => setAppStoreId(e.target.value)}
                      />
                    </Box>
                    <Box>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy || iosBundleVal.trim() === ''}
                        onClick={() =>
                          createApps.mutate({
                            bundle_id: iosBundleVal.trim(),
                            app_store_id: appStoreId.trim() || undefined,
                          })
                        }
                      >
                        Create iOS app
                      </Button>
                    </Box>
                  </Stack>
                )}

                {/* -------------------------- Step 3 · Testers -------------------------- */}
                <Divider textAlign="left">
                  <Typography variant="caption" color="text.secondary">
                    Step 3 · App Distribution testers
                  </Typography>
                </Divider>
                <Stack spacing={0.5}>
                  {testers.map((t, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        fullWidth
                        type="email"
                        label="Tester email"
                        value={t}
                        disabled={busy}
                        onChange={(e) => setTester(i, e.target.value)}
                      />
                      <IconButton
                        size="small"
                        aria-label="Remove tester"
                        disabled={busy}
                        onClick={() => removeTester(i)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button size="small" disabled={busy} onClick={addTester}>
                      Add tester
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy || !testersDirty}
                      onClick={() => saveTesters.mutate()}
                    >
                      Save testers
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Testers are registered on the project and added to the{' '}
                    <strong>{detail.tester_group_name ?? 'Curated Planet'}</strong> group. Firebase emails the
                    invite when the first build is distributed.
                  </Typography>
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}
